import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/joy';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { getDocument, TextLayer } from 'pdfjs-dist';
import { initPdfJsWorker } from '../lib/pdfjs/setupWorker';

initPdfJsWorker();

const PDFViewer = ({
  src,
  page = 1,
  zoom = 100,
  onPageChange,
  onTotalPagesChange,
  onTextSelect,
}) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const textLayerRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selection, setSelection] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState(null);

  // Load PDF document
  useEffect(() => {
    console.log('[PDFViewer] useEffect triggered, src:', src ? 'present' : 'missing');
    if (!src) return;

    setLoading(true);
    setError(null);

    const loadPDF = async () => {
      try {
        console.log('[PDFViewer] Loading PDF from:', src?.substring(0, 100));
        let loadingTask;

        // For blob URLs or data URLs, pdfjs can handle directly
        if (src.startsWith('blob:') || src.startsWith('data:')) {
          console.log('[PDFViewer] Using direct URL (blob/data)');
          loadingTask = getDocument(src);
        } else {
          // Fetch PDF as blob to avoid CORS issues with S3 presigned URLs
          console.log('[PDFViewer] Fetching PDF as ArrayBuffer...');
          const response = await fetch(src);
          console.log('[PDFViewer] Fetch response:', response.status, response.statusText);
          if (!response.ok) {
            throw new Error(`Failed to fetch PDF: ${response.status}`);
          }
          const arrayBuffer = await response.arrayBuffer();
          console.log('[PDFViewer] ArrayBuffer size:', arrayBuffer.byteLength);
          loadingTask = getDocument({ data: arrayBuffer });
        }

        console.log('[PDFViewer] Waiting for PDF to load...');
        const pdf = await loadingTask.promise;
        console.log('[PDFViewer] PDF loaded, pages:', pdf.numPages);
        setPdfDoc(pdf);
        onTotalPagesChange?.(pdf.numPages);
        setLoading(false);
      } catch (err) {
        console.error('[PDFViewer] Error loading PDF:', err);
        setError('Failed to load PDF document');
        setLoading(false);
      }
    };

    loadPDF();

    return () => {
      setPdfDoc(null);
    };
  }, [src, onTotalPagesChange]);

  // Render page
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;

    const renderPage = async () => {
      try {
        const pdfPage = await pdfDoc.getPage(page);
        const scale = zoom / 100;
        const viewport = pdfPage.getViewport({ scale: scale * 1.5 }); // Higher resolution

        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        canvas.height = viewport.height;
        canvas.width = viewport.width;
        canvas.style.width = `${viewport.width / 1.5}px`;
        canvas.style.height = `${viewport.height / 1.5}px`;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        await pdfPage.render(renderContext).promise;

        // Render text layer for selection
        if (textLayerRef.current) {
          const textContent = await pdfPage.getTextContent();
          textLayerRef.current.innerHTML = '';

          const displayViewport = pdfPage.getViewport({ scale: scale });
          textLayerRef.current.style.width = `${displayViewport.width}px`;
          textLayerRef.current.style.height = `${displayViewport.height}px`;

          // Use TextLayer class (pdfjs-dist v4.x API)
          const textLayer = new TextLayer({
            textContentSource: textContent,
            container: textLayerRef.current,
            viewport: displayViewport,
          });
          await textLayer.render();
        }
      } catch (err) {
        console.error('Error rendering page:', err);
      }
    };

    renderPage();
  }, [pdfDoc, page, zoom]);

  // Handle text selection
  const handleMouseUp = useCallback(() => {
    setTimeout(() => {
      const selectedText = window.getSelection()?.toString().trim();

      if (!selectedText || selectedText.length === 0) {
        setSelection(null);
        setPopoverPosition(null);
        return;
      }

      // Check if selection is within the text layer
      const sel = window.getSelection();
      if (!sel?.rangeCount) return;

      const range = sel.getRangeAt(0);
      if (!textLayerRef.current?.contains(range.commonAncestorContainer)) {
        setSelection(null);
        setPopoverPosition(null);
        return;
      }

      const rect = range.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();

      if (containerRect) {
        setSelection(selectedText);
        setPopoverPosition({
          top: rect.top - containerRect.top - 40,
          left: rect.left - containerRect.left + rect.width / 2,
        });
      }
    }, 10);
  }, []);

  const handleMouseDown = useCallback(() => {
    setSelection(null);
    setPopoverPosition(null);
  }, []);

  const handleQuote = useCallback(() => {
    if (selection && onTextSelect) {
      onTextSelect({ text: selection });
    }
    window.getSelection()?.removeAllRanges();
    setSelection(null);
    setPopoverPosition(null);
  }, [selection, onTextSelect]);

  // Close popover on scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setSelection(null);
      setPopoverPosition(null);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
        <CircularProgress size="lg" />
        <Typography level="body-sm" sx={{ mt: 2 }}>
          Loading PDF...
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
        <Typography level="body-lg" color="danger">
          {error}
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      ref={containerRef}
      onMouseUp={handleMouseUp}
      onMouseDown={handleMouseDown}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 2,
        minHeight: '100%',
        position: 'relative',
        userSelect: 'text',
        bgcolor: 'background.level1',
        borderRadius: 'lg',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          boxShadow: 'lg',
          bgcolor: 'background.surface',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 'md',
        }}
      >
        <canvas ref={canvasRef} />
        <Box
          ref={textLayerRef}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden',
            opacity: 0.2,
            lineHeight: 1,
            '& span': {
              position: 'absolute',
              whiteSpace: 'pre',
              transformOrigin: '0% 0%',
              color: 'transparent',
              '&::selection': {
                bgcolor: 'primary.300',
                color: 'transparent',
              },
            },
          }}
        />
      </Box>

      {/* Selection Popover */}
      {selection && popoverPosition && (
        <Box
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            top: popoverPosition.top,
            left: popoverPosition.left,
            transform: 'translateX(-50%)',
            zIndex: 1000,
            animation: 'fadeIn 0.15s ease-out',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateX(-50%) translateY(4px)' },
              to: { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
            },
          }}
        >
          <Button
            size="sm"
            variant="solid"
            color="neutral"
            startDecorator={<FormatQuoteIcon sx={{ fontSize: 16 }} />}
            onClick={handleQuote}
            sx={{
              boxShadow: 'md',
              fontSize: '12px',
              py: 0.5,
              px: 1.5,
              borderRadius: 'md',
              whiteSpace: 'nowrap',
            }}
          >
            Quote
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PDFViewer;
